import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase, IndianRupee, Clock, Users, CheckCircle,
  BookOpen, MessageCircle, ArrowRight, Star, Zap
} from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const StudentJobsPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Earn Money as a Student | Part-Time Student Jobs Online | AGO Innovations',
    description:
      'Looking to earn money as a student in India? AGO Innovations offers real online work — assignment writing, journal writing, and curriculum tasks — for college students. Work from home, earn per task.',
    keywords:
      'earn money as student, student part time work, online work for students India, earn while studying, work from home student jobs, student earning opportunities, student freelance work India',
    canonical: 'https://agoinnovations.in/student-jobs-earn-money',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Student Earning Program — Learn & Earn',
      provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
      description:
        'AGO Innovations Learn & Earn program allows college students to work on real tasks — assignment writing, journal writing, and curriculum activities — and earn money from home.',
      areaServed: 'IN',
      serviceType: 'Student Employment',
    },
  });

  const howItWorks = [
    { step: '01', title: 'Register & Subscribe', desc: 'Create your account and choose the Learn & Earn plan that works for you.' },
    { step: '02', title: 'Browse Available Work', desc: 'See available tasks — assignment writing, journal writing, curriculum, and more.' },
    { step: '03', title: 'Complete & Submit', desc: 'Complete the task as per guidelines and submit before the deadline.' },
    { step: '04', title: 'Get Paid', desc: 'Earnings are credited to your account after task verification and approval.' },
  ];

  const benefits = [
    { icon: <Clock className="w-5 h-5" />, title: 'Flexible Hours', desc: 'Work when you want — mornings, evenings, or weekends around your college schedule.' },
    { icon: <IndianRupee className="w-5 h-5" />, title: 'Real Earnings', desc: 'Earn per completed task. No fixed salary — the more you work, the more you earn.' },
    { icon: <BookOpen className="w-5 h-5" />, title: 'Build Skills', desc: 'Writing, research, and communication skills that help in your own academics and career.' },
    { icon: <Users className="w-5 h-5" />, title: 'Grow Your Network', desc: 'Connect with fellow students and mentors on the AGO platform.' },
    { icon: <Zap className="w-5 h-5" />, title: 'Work From Home', desc: 'No commute. Work from your hostel, home, or anywhere with internet.' },
    { icon: <Star className="w-5 h-5" />, title: 'No Experience Needed', desc: 'We guide you through the process. Good writing and research skills are enough to start.' },
  ];

  const tasks = [
    { title: 'Assignment Writing', desc: 'Complete engineering, medical, B.Sc, and school assignments for customers.', tag: 'High Demand' },
    { title: 'Journal Writing', desc: 'Write academic journals, research summaries, and lab reports.', tag: 'Popular' },
    { title: 'Curriculum Activities', desc: 'Prepare curriculum-based activities and notes for school students.', tag: 'Easy to Start' },
    { title: 'College Projects', desc: 'Help compile, write, and format college project files and reports.', tag: 'Good Pay' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-blue-500/20 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-3xl mx-auto"
        >
          <span className="inline-block bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-5 py-1.5 rounded-full mb-5">
            💼 Learn & Earn — Student Program
          </span>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight">
            Earn Money
            <span className="block text-yellow-400">While Studying</span>
          </h1>
          <p className="text-blue-100 text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            Real online work for college students. Write assignments, journals, and projects for customers.
            Work from home. Get paid per task.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/learn/customer-work')}
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              Start Earning <ArrowRight className="w-5 h-5" />
            </motion.button>
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi!%20I%20want%20to%20know%20about%20the%20Learn%20and%20Earn%20program."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> Ask on WhatsApp
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-blue-200">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Work from Home</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Flexible Hours</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Real Pay per Task</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> No Experience Needed</span>
          </div>
        </motion.div>
      </section>

      {/* AVAILABLE TASKS */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Types of Work Available</h2>
            <p className="text-gray-500 text-lg">Real tasks, real pay — choose what suits your skills.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {tasks.map((task, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">{task.tag}</span>
                </div>
                <h3 className="font-black text-gray-900 text-base mb-2">{task.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{task.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">How It Works</h2>
            <p className="text-gray-500 text-lg">4 simple steps to start earning.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((s, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-purple-50 rounded-2xl p-6 text-center border border-purple-100 relative"
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-700 text-white text-xs font-black px-3 py-1 rounded-full">
                  {s.step}
                </div>
                <h3 className="font-black text-gray-900 text-sm mt-3 mb-2">{s.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Why Join AGO Learn & Earn?</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
              >
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 mb-4">
                  {b.icon}
                </div>
                <h3 className="font-black text-gray-900 text-sm mb-2">{b.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-purple-800 to-indigo-900 text-white text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-4">Ready to Start Earning?</h2>
          <p className="text-purple-200 text-lg mb-8">Join hundreds of students already earning on AGO Innovations.</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => navigate('/learn')}
            className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-10 py-4 rounded-full shadow-lg transition-colors text-lg"
          >
            Join Learn & Earn Now
          </motion.button>
        </motion.div>
      </section>
    </div>
  );
};

export default StudentJobsPage;
